import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { EmdChartBase } from '../emd-chart-base';
import { EmdTooltipRow } from '../emd-chart-tooltip.component';
import {
  EmdPeriod,
  createZoomBrush,
  emdDateTickFormat,
  emdSafeId,
} from '../emd-chart-utils';

export interface EmdSeriesDatum { period: string; series: string; value: number; }

interface LegendEntry { key: string; label: string; full: string; color: string; }

@Component({
    selector: 'emd-multi-line-trend-chart',
    templateUrl: './emd-multi-line-trend-chart.component.html',
    styleUrls: ['./emd-multi-line-trend-chart.component.scss'],
    standalone: false
})
export class EmdMultiLineTrendChartComponent extends EmdChartBase {
  @Input() data: EmdSeriesDatum[] = [];
  @Input() colors: Record<string, string> = {};
  @Input() dateFormat: EmdPeriod = 'M';
  @Input() chartHeight = 468;
  @Input() loading = false;
  /** Metric name shown in the tooltip header, e.g. 'Quantity Shipped'. */
  @Input() valueLabel = 'Quantity Shipped';
  /** Effective cross-filter selection (raw series names); others dim. */
  @Input() selectedSeries: string[] = [];

  /** Line click stages a selection on the series (Qlik-style). */
  @Output() seriesClick = new EventEmitter<{ series: string; ctrlKey: boolean }>();

  @ViewChild('chart', { static: true }) chartRef!: ElementRef<HTMLDivElement>;

  tooltipVisible = false;
  tooltipTitle = '';
  tooltipRows: EmdTooltipRow[] = [];
  tooltipX = 0;
  tooltipY = 0;

  legend: LegendEntry[] = [];
  hidden = new Set<string>();

  private visibleDomain: [Date, Date] | null = null;
  private allDates: Date[] = [];
  private focusIndex = -1;

  // Draggable y-axis range clamp (green max / grey min handles). undefined = auto.
  private yMin?: number;
  private yMax?: number;
  private yAutoMax = 1;

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['dateFormat']) {
      // New/re-bucketed data moves the axes; a stale brush window or y-clamp
      // would clip it, so reset both.
      this.visibleDomain = null;
      this.yMin = this.yMax = undefined;
    }
    super.ngOnChanges(changes);
  }

  // Original line-chart margins were { left: 75, right: 40, top: 70, bottom: 10,
  // brush: 52, mid: 80 } inside a 500px SVG -> a 288px plot. The 70px top band
  // held the in-SVG legend; the legend is now HTML above the SVG (~32px incl.
  // its margin), so `top` drops to 26. 468 - 26 - 22 - (52 + 80) = 288.
  private readonly margin = { top: 26, right: 40, bottom: 22, left: 75 };
  private readonly brushHeight = 52;
  private readonly brushGap = 80;

  constructor(host: ElementRef<HTMLElement>) {
    super(host);
  }

  get hasData(): boolean {
    return this.data.length > 0;
  }

  get showBrush(): boolean {
    // The original always rendered the range strip; keeping it avoids the
    // height budget jumping when a width guard trips.
    return this.allDates.length > 2;
  }

  get ariaLabel(): string {
    if (!this.hasData) { return 'Trend chart, no data'; }
    return `Multi-series trend over ${this.allDates.length} periods. Series: ${this.legend
      .map((l) => l.label)
      .join(', ')}.`;
  }

  toggleSeries(key: string): void {
    if (this.hidden.has(key)) {
      this.hidden.delete(key);
    } else {
      if (this.legend.length - this.hidden.size <= 1) { return; }
      this.hidden.add(key);
    }
    this.render();
  }

  emphasise(key: string | null): void {
    // Resting opacity honours the cross-filter selection, so ending a hover
    // preview does not wipe the dimming of unselected series.
    const selected = new Set(this.selectedSeries.map((s) => emdSafeId(s)));
    const resting = (el: Element) =>
      selected.size && !selected.has(el.getAttribute('data-key') ?? '') ? 0.15 : 1;

    const svg = d3.select(this.chartRef.nativeElement).select('svg');
    svg.selectAll<SVGPathElement, any>('.emd-line').attr('opacity', function () {
      if (key) { return this.getAttribute('data-key') === key ? 1 : 0.15; }
      return resting(this);
    });
    svg.selectAll<SVGCircleElement, any>('.emd-dot').attr('opacity', function () {
      if (key) { return this.getAttribute('data-key') === key ? 1 : 0.15; }
      return resting(this);
    });
  }

  protected render(): void {
    const el = this.chartRef?.nativeElement;
    if (!el || !this.width) { return; }

    d3.select(el).selectAll('svg').remove();
    this.buildLegend();

    if (!this.hasData) { return; }

    this.allDates = Array.from(new Set(this.data.map((d) => d.period)))
      .map((p) => new Date(p))
      .sort((a, b) => a.getTime() - b.getTime());

    if (!this.allDates.length) { return; }

    const chartHeight = this.effectiveHeight(this.chartHeight);
    const brushSpace = this.showBrush ? this.brushHeight + this.brushGap : 0;
    const innerWidth = Math.max(10, this.width - this.margin.left - this.margin.right);
    const innerHeight = Math.max(
      60,
      chartHeight - this.margin.top - this.margin.bottom - brushSpace
    );

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', this.width)
      .attr('height', chartHeight)
      .attr('role', 'presentation')
      .style('background-color', 'white');

    const main = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    const xAll = d3
      .scaleTime()
      .domain(d3.extent(this.allDates) as [Date, Date])
      .range([0, innerWidth]);

    const domain = this.visibleDomain ?? (d3.extent(this.allDates) as [Date, Date]);
    const x = d3.scaleTime().domain(domain).range([0, innerWidth]);

    // Persistent layers above `main` for interaction + handles, so drawMain()'s
    // wipe on each brush/clamp redraw never removes the hover overlay or the
    // handles (which would otherwise kill hover, clicks, and drag mid-gesture).
    const overlayLayer = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
    const handlesLayer = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    const redraw = () => {
      this.drawMain(main, x, innerWidth, innerHeight);
      this.positionAxisHandles(handlesLayer, innerHeight);
    };
    this.drawMain(main, x, innerWidth, innerHeight);

    if (this.showBrush) {
      const brushGroup = svg
        .append('g')
        .attr(
          'transform',
          `translate(${this.margin.left},${this.margin.top + innerHeight + this.brushGap})`
        );

      createZoomBrush({
        group: brushGroup,
        width: innerWidth,
        height: this.brushHeight,
        xScaleAll: xAll,
        dates: this.allDates,
        drawPreview: (g, scale, height) => this.drawPreview(g, scale, height),
        onBrush: (next) => {
          this.visibleDomain = next;
          x.domain(next);
          redraw();
        },
      });
    }

    this.attachPointer(svg, overlayLayer, x, innerWidth, innerHeight);
    this.drawAxisHandles(handlesLayer, innerHeight, redraw);
  }

  private visibleRows(): EmdSeriesDatum[] {
    return this.data.filter((d) => !this.hidden.has(emdSafeId(d.series)));
  }

  private drawMain(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    innerWidth: number,
    innerHeight: number
  ): void {
    g.selectAll('*').remove();

    const [from, to] = x.domain();
    const allRows = this.visibleRows();
    const rows = allRows.filter((d) => {
      const date = new Date(d.period);
      return date >= from && date <= to;
    });

    // Original: domain([0, yMax]) computed over the whole dataset with a floor
    // of 30, no .nice() and no headroom — so brushing changes x only and the
    // lines never jump vertically as the window is dragged. autoMax is kept for
    // the handles; the domain honours a manual clamp when one is set.
    this.yAutoMax = Math.max(30, d3.max(allRows, (d) => d.value) ?? 0);
    const y = d3
      .scaleLinear()
      .domain([this.yMin ?? 0, this.yMax ?? this.yAutoMax])
      .range([innerHeight, 0]);

    // Original drew a labelled tick at every data date and stretched each tick
    // line to full plot height, so it doubles as the vertical gridline.
    const xTicks = this.allDates.filter((d) => d >= from && d <= to);

    const xAxisG = g
      .append('g')
      .attr('class', 'emd-axis emd-axis--x')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(xTicks)
          .tickPadding(3)
          .tickFormat(emdDateTickFormat(this.dateFormat) as any)
      );

    xAxisG
      .selectAll('line')
      .style('pointer-events', 'none')
      .attr('y1', 0)
      .attr('y2', -innerHeight)
      .style('stroke', '#F0F3F6');

    // Tilt and shrink labels once the widest one exceeds the band width.
    const band = xTicks.length > 1 ? x(xTicks[1]) - x(xTicks[0]) : innerWidth;
    let maxTextWidth = 0;
    xAxisG.selectAll<SVGTextElement, unknown>('text').each(function () {
      maxTextWidth = Math.max(maxTextWidth, this.getBBox().width);
    });

    if (maxTextWidth > band - 5) {
      xAxisG
        .selectAll('text')
        .style('text-anchor', 'end')
        .style('font-size', `${band < 14 ? Math.max(6, band - 2) : 12}px`)
        .attr('dy', 3)
        .attr('transform', 'rotate(-45)');
    } else {
      xAxisG
        .selectAll('text')
        .style('text-anchor', 'middle')
        .attr('dy', 3)
        .attr('transform', 'rotate(0)');
    }

    g.append('g')
      .attr('class', 'emd-axis')
      .call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickSize(0)
          .tickPadding(6)
          // Original: d3.format('-2~s') with the origin label suppressed.
          .tickFormat((v) => (+v === 0 ? '' : d3.format('-2~s')(+v)))
      );

    const lineGen = d3
      .line<EmdSeriesDatum>()
      .curve(d3.curveMonotoneX)
      .x((d) => x(new Date(d.period)))
      .y((d) => y(d.value));

    const anySelected = this.selectedSeries.length > 0;

    // Lines/dots draw into a plot-clipped subgroup so a clamped y-axis keeps
    // them inside the plot instead of overshooting into the legend/axis band.
    const clipId = `${this.uid}-plot-clip`;
    g.append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', innerHeight);
    const plot = g.append('g').attr('clip-path', `url(#${clipId})`);

    d3.group(rows, (d) => d.series).forEach((seriesRows, series) => {
      const sorted = [...seriesRows].sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
      );
      const color = this.colorFor(series);
      const key = emdSafeId(series);
      const dimmed = anySelected && this.selectedSeries.indexOf(series) === -1;

      plot.append('path')
        .attr('class', 'emd-line')
        .attr('data-key', key)
        .datum(sorted)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 4)
        .attr('opacity', dimmed ? 0.15 : 1)
        .attr('d', lineGen);

      // Solid series-colour dots with a white ring, hidden until the cursor
      // lands on that date.
      plot.selectAll(`circle.emd-dot-${key}`)
        .data(sorted)
        .enter()
        .append('circle')
        .attr('class', `emd-dot emd-dot-${key}`)
        .attr('data-key', key)
        .attr('cx', (d) => x(new Date(d.period)))
        .attr('cy', (d) => y(d.value))
        .attr('r', 4)
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .attr('opacity', dimmed ? 0.15 : 1)
        .attr('pointer-events', 'none')
        .attr('visibility', 'hidden');
    });

    this.yScale = y;
  }

  private yScale?: d3.ScaleLinear<number, number>;

  private drawPreview(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    height: number
  ): void {
    const rows = this.visibleRows();
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(rows, (d) => d.value) ?? 1])
      .range([height, 2]);

    const lineGen = d3
      .line<EmdSeriesDatum>()
      .curve(d3.curveMonotoneX)
      .x((d) => x(new Date(d.period)))
      .y((d) => y(d.value));

    d3.group(rows, (d) => d.series).forEach((seriesRows, series) => {
      const sorted = [...seriesRows].sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
      );
      const color = this.colorFor(series);

      g.append('path')
        .datum(sorted)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 4)
        .attr('d', lineGen);

      // Match the main plot's dot markers inside the range strip.
      g.selectAll(`circle.emd-brush-dot-${emdSafeId(series)}`)
        .data(sorted)
        .enter()
        .append('circle')
        .attr('cx', (d) => x(new Date(d.period)))
        .attr('cy', (d) => y(d.value))
        .attr('r', 2)
        .attr('fill', color)
        .attr('pointer-events', 'none');
    });
  }

  private attachPointer(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleTime<number, number>,
    innerWidth: number,
    innerHeight: number
  ): void {
    const crosshair = layer
      .append('line')
      .attr('class', 'emd-crosshair')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('opacity', 0);

    // Full-plot capture surface for hover AND click. Sitting above the lines,
    // it must own the click too — a per-line handler is unreachable (the overlay
    // covers the lines). A click picks the nearest series at the pointer.
    const overlay = layer
      .append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .style('cursor', this.seriesClick.observers.length ? 'pointer' : 'default');

    const showAt = (date: Date, clientX: number, clientY: number) => {
      const nearest = this.nearestDate(date, x.domain() as [Date, Date]);
      if (!nearest) { return; }

      crosshair.attr('x1', x(nearest)).attr('x2', x(nearest)).style('opacity', 1);
      // Dots live in the redraw layer (sibling of this overlay layer).
      svg.selectAll('.emd-dot').attr('visibility', (d: any) =>
        new Date(d.period).getTime() === nearest.getTime() ? 'visible' : 'hidden'
      );

      this.buildTooltip(nearest);
      const rect = this.host.nativeElement.getBoundingClientRect();
      this.tooltipX = clientX - rect.left;
      this.tooltipY = clientY - rect.top;
      this.tooltipVisible = true;
    };

    overlay
      .on('mousemove', (event: MouseEvent) => {
        const [mx] = d3.pointer(event);
        showAt(x.invert(mx), event.clientX, event.clientY);
      })
      .on('mouseleave', () => {
        crosshair.style('opacity', 0);
        svg.selectAll('.emd-dot').attr('visibility', 'hidden');
        this.tooltipVisible = false;
      })
      // Click selects the series nearest the pointer at the nearest date.
      .on('click', (event: MouseEvent) => {
        if (!this.seriesClick.observers.length || !this.yScale) { return; }
        const [mx, my] = d3.pointer(event);
        const nearest = this.nearestDate(x.invert(mx), x.domain() as [Date, Date]);
        if (!nearest) { return; }
        const key = nearest.getTime();
        let best: { series: string; dist: number } | null = null;
        for (const row of this.visibleRows()) {
          if (new Date(row.period).getTime() !== key) { continue; }
          const dist = Math.abs(this.yScale(row.value) - my);
          if (!best || dist < best.dist) { best = { series: row.series, dist }; }
        }
        if (best) {
          this.seriesClick.emit({ series: best.series, ctrlKey: event.ctrlKey || event.metaKey });
        }
      });

    this.keyboardStep = (delta: number) => {
      const dates = this.datesInWindow(x.domain() as [Date, Date]);
      if (!dates.length) { return; }
      this.focusIndex = Math.max(0, Math.min(dates.length - 1, this.focusIndex + delta));
      const target = dates[this.focusIndex];
      const rect = this.host.nativeElement.getBoundingClientRect();
      showAt(
        target,
        rect.left + this.margin.left + x(target),
        rect.top + this.margin.top + innerHeight / 2
      );
    };

    this.keyboardClear = () => {
      crosshair.style('opacity', 0);
      svg.selectAll('.emd-dot').attr('visibility', 'hidden');
      this.tooltipVisible = false;
      this.focusIndex = -1;
    };
  }

  private keyboardStep?: (delta: number) => void;
  private keyboardClear?: () => void;

  // ── Y-axis range handles ─────────────────────────────────────────────────────
  // Green (max, top) / grey (min, bottom) circles on the single y-axis that
  // clamp its visible range, matching the combo-bar-card's range sliders. They
  // live in a persistent layer, so drawMain's redraws don't wipe them mid-drag.

  private axisRefScale(innerHeight: number): d3.ScaleLinear<number, number> | null {
    if (!(this.yAutoMax > 0)) { return null; }
    // Full-extent scale, independent of the current clamp, so a handle's pixel
    // position maps to a stable value throughout a drag.
    return d3.scaleLinear().domain([0, this.yAutoMax]).range([innerHeight, 0]);
  }

  private drawAxisHandles(
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    innerHeight: number,
    redraw: () => void
  ): void {
    layer.selectAll('*').remove();
    const ref = this.axisRefScale(innerHeight);
    if (!ref) { return; }

    const autoMax = this.yAutoMax;
    const gap = autoMax * 0.08; // minimum visible band between the handles
    const getMin = () => this.yMin ?? 0;
    const getMax = () => this.yMax ?? autoMax;

    const make = (kind: 'max' | 'min', fill: string) => {
      layer
        .append('circle')
        .attr('class', `emd-axis-handle emd-axis-handle--${kind}`)
        .attr('data-kind', kind)
        .attr('cx', 0)
        .attr('cy', ref(kind === 'max' ? getMax() : getMin()))
        .attr('r', 5.5)
        .attr('fill', fill)
        .style('cursor', 'ns-resize')
        .call(
          d3.drag<SVGCircleElement, unknown>().on('drag', (event) => {
            const v = Math.max(0, Math.min(autoMax, ref.invert(event.y)));
            if (kind === 'max') { this.yMax = Math.max(getMin() + gap, v); }
            else { this.yMin = Math.min(getMax() - gap, v); }
            redraw();
          })
        );
    };

    make('max', '#43C678'); // green, top
    make('min', '#9AA7B8'); // grey, bottom
  }

  /** Re-seat the persistent handles after a redraw (drag or brush). */
  private positionAxisHandles(
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    innerHeight: number
  ): void {
    const ref = this.axisRefScale(innerHeight);
    if (!ref) { return; }
    layer.selectAll<SVGCircleElement, unknown>('.emd-axis-handle').each((_d, i, nodes) => {
      const el = d3.select(nodes[i]);
      const value = el.attr('data-kind') === 'max' ? (this.yMax ?? this.yAutoMax) : (this.yMin ?? 0);
      el.attr('cy', ref(value));
    });
  }

  private datesInWindow([from, to]: [Date, Date]): Date[] {
    return this.allDates.filter((d) => d >= from && d <= to);
  }

  private nearestDate(target: Date, window: [Date, Date]): Date | null {
    const candidates = this.datesInWindow(window);
    if (!candidates.length) { return null; }
    return candidates.reduce((best, d) =>
      Math.abs(d.getTime() - target.getTime()) < Math.abs(best.getTime() - target.getTime())
        ? d
        : best
    );
  }

  private buildTooltip(date: Date): void {
    const key = date.getTime();
    // Original header: '{metric} (05-Jan-2024)'.
    this.tooltipTitle = `${this.valueLabel} (${d3.timeFormat('%d-%b-%Y')(date)})`;

    const rows = this.visibleRows();
    const index = this.allDates.findIndex((d) => d.getTime() === key);
    const previousKey = index > 0 ? this.allDates[index - 1].getTime() : null;

    // Original keeps dateRollup order — no value sort — so rows do not reshuffle
    // as the cursor moves.
    this.tooltipRows = rows
      .filter((d) => new Date(d.period).getTime() === key)
      .map((d) => {
        const prior =
          previousKey === null
            ? undefined
            : rows.find(
                (p) => p.series === d.series && new Date(p.period).getTime() === previousKey
              );
        return {
          label: d.series,
          value: d.value.toLocaleString('en-US', { maximumFractionDigits: 2 }),
          color: this.colorFor(d.series),
          movement:
            prior === undefined || prior.value === d.value
              ? null
              : d.value > prior.value
              ? ('up' as const)
              : ('down' as const),
        };
      });
  }

  /** First entries of the original darkColors palette. */
  private static readonly FALLBACK_COLORS = [
    '#E74028', '#2CB239', '#25D3EB', '#FFB039', '#1566DE', '#7848FF',
    '#7AD045', '#CB04DC', '#FA195C', '#9E0000', '#EACD37', '#DD630B',
  ];

  private colorFor(series: string): string {
    const mapped = this.colors[series];
    if (mapped) { return mapped; }
    const index = Array.from(new Set(this.data.map((d) => d.series))).indexOf(series);
    const palette = EmdMultiLineTrendChartComponent.FALLBACK_COLORS;
    return palette[Math.max(0, index) % palette.length];
  }

  /** Original maxCharacters for a legend label. */
  private static readonly MAX_LEGEND_CHARS = 10;

  private buildLegend(): void {
    const max = EmdMultiLineTrendChartComponent.MAX_LEGEND_CHARS;
    this.legend = Array.from(new Set(this.data.map((d) => d.series))).map((series) => ({
      key: emdSafeId(series),
      label: series.length > max ? `${series.substr(0, max).trim()}..` : series,
      full: series,
      color: this.colorFor(series),
    }));
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.keyboardStep?.(1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.keyboardStep?.(-1);
        break;
      case 'Escape':
        this.keyboardClear?.();
        break;
    }
  }

  onBlur(): void {
    this.keyboardClear?.();
  }
}
